/**
 * MarketDataFootnote — the single, shared explanation for gaps and flags in any
 * market-based financial chart (EV, market cap, EV/EBITDA, WACC, and everything
 * derived from them).
 *
 * These metrics need a traded year-end share price, so a company that was not
 * listed for its earlier fiscal years (a pre-IPO history) has a LEADING GAP: the
 * series starts partway along the axis. Left unexplained, the short remaining
 * segment reads as "the metric was zero for the early years", which is wrong —
 * it isn't plotted at all for those years.
 *
 * This component renders up to three stacked lines, in order:
 *   1. `extra`         — a chart-specific note passed in (e.g. EV capped years).
 *   2. leading-gap     — "Before FYxx, <metric> could not be computed …", naming
 *                        the first year the metric becomes available.
 *   3. other flags     — the generic "years marked ⚠ were adjusted/estimated —
 *                        hover for the reason", for any FLAGGED year that is not
 *                        part of the leading gap (e.g. a split-adjusted year).
 *
 * Every market chart renders exactly this, so the wording, threshold, and hover
 * affordance stay identical across the dashboard. Pairs with `yearTickFactory`
 * (the ⚠ axis flags) and `tooltipNotesForYear` (the per-year hover reason).
 */
import type { ReactNode } from 'react';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';
import { formatFiscalYear } from '../utils/formatters';

interface MarketDataFootnoteProps {
  records: UnifiedFinancialRecord[];
  region?: RegionConfig;
  /** True when THIS chart's metric has a plotted value for the given record. */
  hasMetric: (r: UnifiedFinancialRecord) => boolean;
  /**
   * The metric named in the leading-gap sentence. Phrased to slot after
   * "Before FYxx, …" — e.g. "WACC", "the EV/EBITDA multiple",
   * "EV, market cap and the EV/EBITDA multiple".
   */
  metricLabel: string;
  /** Optional chart-specific note rendered above the standard lines. */
  extra?: ReactNode;
  /**
   * Whether this chart keeps flagged/empty years on the axis as hoverable
   * columns (the trend charts do). When false — e.g. a chart that filters gap
   * years out entirely — the "hover a flagged year" affordance and the generic
   * per-year flag line are suppressed, since there is nothing to hover.
   */
  hoverable?: boolean;
}

export default function MarketDataFootnote({
  records,
  region,
  hasMetric,
  metricLabel,
  extra,
  hoverable = true,
}: MarketDataFootnoteProps) {
  const ascending = [...records].sort((a, b) => a.year - b.year);
  const firstIdx = ascending.findIndex(hasMetric);

  // Leading gap = the run of pre-metric years before the first year that has it.
  // firstIdx === 0 → present from the start (no gap); firstIdx === -1 → the
  // metric is absent everywhere (nothing to caption — the chart itself will have
  // rendered its own empty state).
  const hasLeadingGap = firstIdx > 0;
  const firstLabel =
    firstIdx >= 0 ? formatFiscalYear(ascending[firstIdx].year, region) : null;
  const leadingGapYears = new Set(
    hasLeadingGap ? ascending.slice(0, firstIdx).map((d) => d.year) : [],
  );

  // Other flagged years: carry a data note but sit OUTSIDE the leading gap, so
  // they're a distinct concern (an adjusted/estimated value, not a pre-listing
  // blank). The leading-gap years already carry MARKET_CAP_UNDERIVABLE notes;
  // excluding them here avoids saying the same thing twice.
  const hasOtherFlags =
    hoverable &&
    ascending.some(
      (d) => (d.data_notes?.length ?? 0) > 0 && !leadingGapYears.has(d.year),
    );

  if (!extra && !(hasLeadingGap && firstLabel) && !hasOtherFlags) return null;

  return (
    <div className="mt-2 space-y-1">
      {extra}
      {hasLeadingGap && firstLabel && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1">
          <span aria-hidden="true">⚠</span>
          <span>
            Before {firstLabel}, {metricLabel} could not be computed — those years
            had no traded year-end market price (typically before the company was
            listed), so the value is left blank rather than shown as zero.
            Operating metrics (revenue, EBITDA, ROIC) are unaffected.
            {hoverable ? ' Hover a flagged year for details.' : ''}
          </span>
        </p>
      )}
      {hasOtherFlags && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1">
          <span aria-hidden="true">⚠</span>
          <span>
            Years marked with a warning were adjusted, estimated, or not
            calculated — hover the year for the reason.
          </span>
        </p>
      )}
    </div>
  );
}
